// Tenant Users — dedicated page (Path C, step 5).
//
// Route: /admin/customers/tenants/:tenantId/users
//
// Mirrors UsersPage 1:1 for the design language (the tenants brief
// blesses reuse). Differences from admin Users:
//   - No role picker (V1 = always tenant_admin).
//   - No MFA column (no per-user MFA reset action on the consumer pool
//     in V1).
//   - Adds "Anonymize (GDPR)" terminal action — super_admin-only,
//     surfaced via the detail drawer.
//   - Tenant header (name + back link to tenant detail) above PageHeader.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Avatar,
  Button,
  StatCard,
  TextInput,
} from '@gc-coach/design-system';
import type {
  GetTenantResponse,
  TenantUserStatusFilter,
  TenantUserSummary,
} from '@gc-coach/shared';
import { AdminShell, PageHeader } from '../shell/index.js';
import { useI18n } from '../i18n/index.js';
import { getTenant } from '../api/endpoints.js';
import { InviteTenantUserDrawer } from './InviteTenantUserDrawer.js';
import { TenantUserDetailDrawer } from './TenantUserDetailDrawer.js';
import { useTenantUsers } from './useTenantUsers.js';
import { formatRelativeTime } from '../users/time.js';
import styles from './TenantUsersPage.module.css';

export function TenantUsersPage() {
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const { t, tt } = useI18n();

  const [statusFilter, setStatusFilter] = useState<TenantUserStatusFilter>('all');
  const [q, setQ] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tenantHeader, setTenantHeader] = useState<GetTenantResponse['tenant'] | null>(null);
  const [tenantError, setTenantError] = useState<string | null>(null);

  // Resolve the tenant header (name) for the breadcrumb + page header.
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      const result = await getTenant(tenantId);
      if (cancelled) return;
      if (result.ok) {
        setTenantHeader(result.data.tenant);
      } else {
        setTenantError(t('tenantUsers.tenantNotFound'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, t]);

  const query = useMemo(
    () => ({
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      ...(q.trim() ? { q } : {}),
    }),
    [statusFilter, q],
  );

  const { data, isLoading, refetch } = useTenantUsers(tenantId ?? '', query);
  const users = data?.users ?? [];
  const counts = data?.counts ?? {
    active: 0,
    invited: 0,
    suspended: 0,
    removed: 0,
    anonymized: 0,
  };
  const totalLive = counts.active + counts.invited + counts.suspended;

  const tenantName = tenantHeader?.name ?? '';

  if (!tenantId) {
    return (
      <AdminShell
        activeKey="tenants"
        breadcrumb={[t('tenants.breadcrumbCustomers'), t('tenants.breadcrumbTenants')]}
        state={{ kind: 'normal' }}
      >
        <div className={styles.empty}>
          <strong>{t('tenantUsers.tenantNotFound')}</strong>
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell
      activeKey="tenants"
      breadcrumb={[
        t('tenants.breadcrumbCustomers'),
        t('tenants.breadcrumbTenants'),
        tenantName || '…',
        t('tenantUsers.breadcrumbUsers'),
      ]}
      state={isLoading && !data ? { kind: 'loading' } : { kind: 'normal' }}
    >
      <PageHeader
        title={tenantName ? tt('tenantUsers.titleTemplate', { name: tenantName }) : t('tenantUsers.titleFallback')}
        sub={tt('tenantUsers.subTemplate', {
          active: counts.active,
          invited: counts.invited,
          suspended: counts.suspended,
        })}
        actions={
          <>
            <Button
              kind="ghost"
              onClick={() => navigate(`/admin/customers/tenants?open=${encodeURIComponent(tenantId)}`)}
            >
              {t('tenantUsers.backToTenant')}
            </Button>
            <Button kind="primary" onClick={() => setShowInvite(true)}>
              {t('tenantUsers.inviteButton')}
            </Button>
          </>
        }
      />

      {tenantError && (
        <div className={styles.empty} role="alert">
          {tenantError}
        </div>
      )}

      {/* Stat cards */}
      <div className={styles.statRow}>
        <StatCard
          label={t('tenantUsers.statUsers')}
          value={totalLive.toString()}
          onClick={() => setStatusFilter('all')}
        />
        <StatCard
          label={t('tenantUsers.statActive')}
          value={counts.active.toString()}
          onClick={() => setStatusFilter('active')}
        />
        <StatCard
          label={t('tenantUsers.statInvited')}
          value={counts.invited.toString()}
          onClick={() => setStatusFilter('invited')}
        />
        <StatCard
          label={t('tenantUsers.statSuspended')}
          value={counts.suspended.toString()}
          onClick={() => setStatusFilter('suspended')}
        />
      </div>

      {/* Filters */}
      <div className={styles.filterRow}>
        <div className={styles.chips} role="group" aria-label={t('tenantUsers.chipGroupLabel')}>
          <FilterChip
            label={tt('tenantUsers.chipAllTemplate', { n: totalLive })}
            active={statusFilter === 'all'}
            onClick={() => setStatusFilter('all')}
          />
          <FilterChip
            label={tt('tenantUsers.chipActiveTemplate', { n: counts.active })}
            active={statusFilter === 'active'}
            onClick={() => setStatusFilter('active')}
          />
          <FilterChip
            label={tt('tenantUsers.chipInvitedTemplate', { n: counts.invited })}
            active={statusFilter === 'invited'}
            onClick={() => setStatusFilter('invited')}
          />
          <FilterChip
            label={tt('tenantUsers.chipSuspendedTemplate', { n: counts.suspended })}
            active={statusFilter === 'suspended'}
            onClick={() => setStatusFilter('suspended')}
          />
          <FilterChip
            label={tt('tenantUsers.chipRemovedTemplate', { n: counts.removed })}
            active={statusFilter === 'removed'}
            onClick={() => setStatusFilter('removed')}
          />
          <FilterChip
            label={tt('tenantUsers.chipAnonymizedTemplate', { n: counts.anonymized })}
            active={statusFilter === 'anonymized'}
            onClick={() => setStatusFilter('anonymized')}
          />
        </div>

        <div className={styles.filterRight}>
          <div className={styles.search}>
            <TextInput
              type="search"
              placeholder={t('tenantUsers.searchPlaceholder')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label={t('tenantUsers.searchPlaceholder')}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.colUser}>{t('tenantUsers.colUser')}</th>
              <th>{t('tenantUsers.colRole')}</th>
              <th>{t('tenantUsers.colLastSeen')}</th>
              <th>{t('tenantUsers.colStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && !isLoading && (
              <tr>
                <td colSpan={4} className={styles.empty}>
                  <strong>{t('tenantUsers.emptyTitle')}</strong>
                  <span>{t('tenantUsers.emptyBody')}</span>
                </td>
              </tr>
            )}
            {users.map((user) => (
              <TenantUserRow
                key={user.id}
                user={user}
                onClick={() => setSelectedId(user.id)}
              />
            ))}
          </tbody>
        </table>
        <footer className={styles.tableFooter}>
          {tt('tenantUsers.footerCountTemplate', { n: users.length })}
        </footer>
      </div>

      {showInvite && (
        <InviteTenantUserDrawer
          tenantId={tenantId}
          onClose={() => setShowInvite(false)}
          onInvited={() => {
            setShowInvite(false);
            void refetch();
          }}
        />
      )}

      {selectedId && (
        <TenantUserDetailDrawer
          tenantId={tenantId}
          tenantUserId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            void refetch();
          }}
          onRemoved={() => {
            setSelectedId(null);
            void refetch();
          }}
        />
      )}
    </AdminShell>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────────

function TenantUserRow({
  user,
  onClick,
}: {
  user: TenantUserSummary;
  onClick: () => void;
}) {
  const { t } = useI18n();
  const displayName =
    user.status === 'invited' ? t('tenantUsers.invitedRowName') : user.displayName;
  return (
    <tr className={styles.row} onClick={onClick} tabIndex={0}>
      <td className={styles.cellUser}>
        <Avatar
          name={user.displayName}
          email={user.email}
          size="sm"
          variant={user.status === 'invited' ? 'dashed' : 'filled'}
        />
        <span className={styles.userText}>
          <span
            className={[styles.userName, user.status === 'invited' ? styles.userNameItalic : null]
              .filter(Boolean)
              .join(' ')}
          >
            {displayName}
          </span>
          <span className={styles.userEmail}>{user.email}</span>
        </span>
      </td>
      <td className={styles.cellMono}>{t('tenantUsers.roleTenantAdmin')}</td>
      <td className={styles.cellMuted}>
        {user.lastSeenAt ? formatRelativeTime(user.lastSeenAt) : '—'}
      </td>
      <td className={statusClass(user.status, styles)}>{statusText(user.status, t)}</td>
    </tr>
  );
}

function statusClass(s: TenantUserSummary['status'], cls: Record<string, string>): string {
  if (s === 'active') return cls['statusActive'] ?? '';
  if (s === 'invited') return cls['statusInvited'] ?? '';
  if (s === 'suspended') return cls['statusSuspended'] ?? '';
  // removed + anonymized share the muted style.
  return cls['statusSuspended'] ?? '';
}

function statusText(
  s: TenantUserSummary['status'],
  t: (k: string) => string,
): string {
  if (s === 'active') return t('tenantUsers.statusActive');
  if (s === 'invited') return t('tenantUsers.statusInvited');
  if (s === 'suspended') return t('tenantUsers.statusSuspended');
  if (s === 'removed') return t('tenantUsers.statusRemoved');
  return t('tenantUsers.statusAnonymized');
}

// ─── Filter chip ─────────────────────────────────────────────────────────────

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={[styles.chip, active ? styles.chipActive : null].filter(Boolean).join(' ')}
      onClick={onClick}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}
